<section
  class="scale-panel"
  data-testid="scale-calibration-panel"
  [attr.data-view]="view"
  [attr.data-placement]="placement"
  [class.placement-right]="placement === 'right'"
  role="region"
  [attr.aria-label]="view === 'calibrate' ? 'Calibrate scale' : 'Scale calibration details'"
  (mousedown)="$event.stopPropagation()"
>
  @if (busy) {
    <mat-progress-bar mode="indeterminate" class="panel-progress"></mat-progress-bar>
  }

  @if (view === 'calibrate') {
    <header class="panel-header">
      <mat-icon aria-hidden="true">architecture</mat-icon>
      <h2 #heading tabindex="-1" data-testid="calibration-title">Calibrate scale</h2>
      <button
        mat-icon-button
        type="button"
        class="panel-move"
        data-testid="calibration-move-panel"
        aria-label="Move this panel to the other side"
        (click)="movePanel.emit()"
      >
        <mat-icon>swap_horiz</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        class="panel-close"
        data-testid="calibration-cancel-icon"
        aria-label="Cancel calibration"
        (click)="cancelCalibration.emit()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <form class="panel-body" (ngSubmit)="onSubmit()" novalidate>
      <!-- Step 1: the line -->
      <div class="step" [class.done]="!!line">
        <p class="step-title">
          <span class="step-number" aria-hidden="true">1</span> Mark both ends of a known dimension.
        </p>
        @if (!line) {
          <p class="step-help" data-testid="calibration-instructions">
            Click or tap each end of a dimension line printed on the plan.
          </p>
          <button mat-stroked-button type="button" data-testid="calibration-place-line" (click)="placeLine.emit()">
            Place a line for me
          </button>
          <p class="step-help">Then move each end with the arrow keys, or drag it.</p>
        } @else {
          <p class="step-help" data-testid="calibration-line-status" role="status">
            Line placed: {{ sheetLengthText }}. Drag an end, or focus it and use the arrow keys, to adjust.
          </p>
          <button mat-button type="button" data-testid="calibration-redraw-line" (click)="redrawLine.emit()">
            Trace it again
          </button>
          @if (preview?.tooShort) {
            <p class="panel-alert" role="alert" data-testid="calibration-line-too-short">
              The two ends are too close together to calibrate from. Trace a longer dimension.
            </p>
          }
        }
      </div>

      <!--
        The rest of the form appears once both ends are placed. Until then the panel stays small,
        so it covers as little of the drawing as possible while the reviewer is looking for a
        dimension to trace.
      -->
      @if (line) {
        <!-- Step 2: the length -->
        <div class="step">
          <p class="step-title">
            <span class="step-number" aria-hidden="true">2</span> Enter the length printed on the plan.
          </p>
          <mat-form-field appearance="outline" class="length-field" subscriptSizing="dynamic">
            <mat-label>Known length</mat-label>
            <input
              #lengthInput
              matInput
              name="knownLength"
              type="text"
              inputmode="text"
              autocomplete="off"
              spellcheck="false"
              data-testid="calibration-length-input"
              placeholder="e.g. 11' or 3.35 m"
              [ngModel]="enteredValue"
              (ngModelChange)="onLengthInput($event)"
              [attr.aria-invalid]="lengthError ? 'true' : 'false'"
              aria-describedby="calibration-length-help"
            />
          </mat-form-field>
          <div id="calibration-length-help" class="length-help" aria-live="polite">
            @if (lengthError) {
              <p class="panel-alert" data-testid="calibration-length-error">{{ lengthError }}</p>
            } @else if (normalizedLength) {
              <p class="length-normalized" data-testid="calibration-length-normalized">
                Will be saved as <strong>{{ normalizedLength }}</strong>
              </p>
            } @else {
              <p class="step-help">Include the unit: 11', 11'-6", 132 in, 3.35 m, 335 cm or 3350 mm.</p>
            }
          </div>
          <mat-form-field appearance="outline" class="note-field" subscriptSizing="dynamic">
            <mat-label>Note (optional)</mat-label>
            <input
              matInput
              name="calibrationNote"
              type="text"
              maxlength="500"
              autocomplete="off"
              data-testid="calibration-note-input"
              placeholder="Which dimension you used"
              [(ngModel)]="note"
            />
          </mat-form-field>
        </div>

        <!-- Step 3: the summary -->
        @if (summaryRows.length) {
          <div class="step" data-testid="calibration-summary">
            <p class="step-title"><span class="step-number" aria-hidden="true">3</span> Check the result.</p>
            <dl class="summary">
              @for (row of summaryRows; track row.testId) {
                <div class="summary-row">
                  <dt>{{ row.label }}</dt>
                  <dd [attr.data-testid]="row.testId">{{ row.value }}</dd>
                </div>
              }
            </dl>
          </div>
        }

        @if (warnings.length) {
          <div class="panel-warning" role="alert" data-testid="calibration-warnings">
            @for (warning of warnings; track warning.code) {
              <p><mat-icon aria-hidden="true">warning</mat-icon> {{ warning.message }}</p>
            }
            <mat-checkbox
              name="acknowledgeWarnings"
              data-testid="calibration-acknowledge"
              [(ngModel)]="warningsAcknowledged"
            >
              I have checked the line and the length
            </mat-checkbox>
          </div>
        }

        @if (serverMessage) {
          <p class="panel-alert" role="alert" data-testid="calibration-server-message">{{ serverMessage }}</p>
        }

        <ul class="panel-notes" data-testid="calibration-limitations">
          <li>
            This calibration applies to the whole page. It is only right for views drawn at the same scale; details
            drawn at another scale will measure wrong.
          </li>
          <li>
            One reference sets a single scale in every direction. It cannot correct a scan that is stretched or skewed
            differently along its width and height.
          </li>
        </ul>
      }

      <div class="panel-footer" data-testid="calibration-footer">
        <div class="panel-actions">
          <button mat-button type="button" data-testid="calibration-cancel" (click)="cancelCalibration.emit()">
            Cancel
          </button>
          @if (line) {
            <button
              mat-flat-button
              color="primary"
              type="submit"
              data-testid="calibration-apply"
              data-cuj-effect="persistent-page-mutation"
              [disabled]="!canApply"
            >
              Apply calibration
            </button>
          }
        </div>
        <p class="step-help escape-hint">Press Esc to cancel without changing anything.</p>
      </div>
    </form>
  } @else {
    <header class="panel-header">
      <mat-icon aria-hidden="true">{{ state?.status === 'invalidated' ? 'warning' : 'verified' }}</mat-icon>
      <h2 #heading tabindex="-1" data-testid="calibration-details-title">
        {{ state?.status === 'invalidated' ? 'Calibration not applied' : 'Manually calibrated' }}
      </h2>
      <button
        mat-icon-button
        type="button"
        class="panel-move"
        data-testid="calibration-move-panel"
        aria-label="Move this panel to the other side"
        (click)="movePanel.emit()"
      >
        <mat-icon>swap_horiz</mat-icon>
      </button>
      <button
        mat-icon-button
        type="button"
        class="panel-close"
        data-testid="calibration-details-close"
        aria-label="Close calibration details"
        (click)="closeDetails.emit()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="panel-body">
      @if (state?.message) {
        <p class="panel-alert" role="alert" data-testid="calibration-invalidated-message">{{ state?.message }}</p>
      }
      @if (comparing) {
        <p class="panel-info" role="status" data-testid="calibration-comparing-message">
          Measurements are temporarily using the detected scale ({{ detectedScaleText }}). The manual calibration is
          still saved.
        </p>
      }

      <dl class="summary" data-testid="calibration-details">
        @for (row of detailRows; track row.testId) {
          <div class="summary-row">
            <dt>{{ row.label }}</dt>
            <dd [attr.data-testid]="row.testId">{{ row.value }}</dd>
          </div>
        }
        @if (state?.calibration; as calibration) {
          <div class="summary-row">
            <dt>Calibrated by</dt>
            <dd data-testid="calibration-detail-author">
              {{ calibration.createdBy || 'Unknown' }}
              @if (calibration.createdAt) {
                <span class="detail-date">on {{ calibration.createdAt | date: 'medium' }}</span>
              }
            </dd>
          </div>
          <div class="summary-row">
            <dt>Version</dt>
            <dd data-testid="calibration-detail-revision">{{ state?.revision }}</dd>
          </div>
        }
      </dl>

      <ul class="panel-notes">
        <li>
          This calibration applies to the whole page. It is only right for views drawn at the same scale; details drawn
          at another scale will measure wrong.
        </li>
      </ul>

      @if (serverMessage) {
        <p class="panel-alert" role="alert" data-testid="calibration-server-message">{{ serverMessage }}</p>
      }

      <div class="panel-actions wrap">
        <button
          mat-stroked-button
          type="button"
          data-testid="calibration-details-recalibrate"
          [disabled]="busy"
          (click)="recalibrate.emit()"
        >
          Recalibrate
        </button>
        @if (state?.status === 'manual') {
          <button
            mat-stroked-button
            type="button"
            data-testid="calibration-details-compare"
            [attr.aria-pressed]="comparing ? 'true' : 'false'"
            [disabled]="busy"
            (click)="toggleCompare.emit()"
          >
            {{ comparing ? 'Use manual scale' : 'Compare with detected scale' }}
          </button>
        }
        <button
          mat-stroked-button
          type="button"
          data-testid="calibration-details-reset"
          data-cuj-effect="persistent-page-mutation"
          [disabled]="busy"
          (click)="resetToAutomatic.emit()"
        >
          Reset to automatic
        </button>
      </div>

      <h3 class="history-title">Change history</h3>
      @if (history === null) {
        <p class="step-help" data-testid="calibration-history-loading">Loading…</p>
      } @else if (!history.length) {
        <p class="step-help">No changes recorded.</p>
      } @else {
        <ol class="history" data-testid="calibration-history">
          @for (entry of history; track entry.revision) {
            <li>
              <span class="history-what">
                {{ entry.action === 'reset' ? 'Reset to automatic' : 'Calibrated' }}
                @if (entry.calibration) {
                  — {{ entry.calibration.normalizedValue }} → {{ historyScale(entry) }}
                }
              </span>
              <span class="history-who">
                Version {{ entry.revision }} · {{ entry.actor || 'Unknown' }}
                @if (entry.occurredAt) {
                  · {{ entry.occurredAt | date: 'medium' }}
                }
              </span>
              @if (entry.note) {
                <span class="history-note">{{ entry.note }}</span>
              }
            </li>
          }
        </ol>
      }
    </div>
  }
</section>
